// Foto, cartão e edição do contato nas Mensagens (0.38).
import { useEffect, useState } from 'react';
import { ActivityIndicator, Alert, Image, Linking, Platform, Pressable, ScrollView, Text, View } from 'react-native';
import { FRASE_MAX, meuContato, salvarMeuContato, soDigitos, tirarMinhaFoto, trocarMinhaFoto, urlFoto, useContato, type Contato } from '../lib/contato';
import { semBloqueio } from '../auth/AuthContext';
import { useAnexar } from './MenuAnexo';
import type { OrigemAnexo } from '../lib/anexar';
import { useTecladoAndroid, ROLAGEM_TECLADO } from './teclado';
import { VerFoto } from './VerFoto';
import { ActionSheet, Btn, Field, Sheet, type Action } from './ui';
import { C, alpha } from './theme';
import { t } from '../lib/i18n';

const letra = (n: string) => (n || '?').trim().charAt(0).toUpperCase();

/** Bolinha com a foto da pessoa (ou a inicial do nome). */
export function Avatar({ id, nome, tam = 48, cor, icone, foto: fotoFixa, ampliar }: { id?: string | null; nome: string; tam?: number; cor: string; icone?: string; foto?: string | null; ampliar?: boolean }) {
  const c = useContato(fotoFixa === undefined ? id : null);
  const path = fotoFixa !== undefined ? fotoFixa : c?.foto;
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => { let vivo = true; setUrl(null); if (path) urlFoto(path).then(u => vivo && setUrl(u)); return () => { vivo = false; }; }, [path]);
  const [grande, setGrande] = useState(false);
  const bola = (
    <View style={{ width: tam, height: tam, borderRadius: tam / 2, backgroundColor: alpha(cor, 0.22), alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
      {url ? <Image source={{ uri: url }} style={{ width: tam, height: tam }} resizeMode="cover" />
        : <Text style={{ color: C.text, fontSize: tam * 0.42, fontWeight: '800' }}>{icone ?? letra(nome)}</Text>}
    </View>
  );
  if (!ampliar || !url) return bola;
  return (<>
    <Pressable onPress={() => setGrande(true)} accessibilityLabel={t('Ampliar a foto')}>{bola}</Pressable>
    <VerFoto url={grande ? url : null} titulo={nome} onClose={() => setGrande(false)} />
  </>);
}

/** Cartão de contato de outra pessoa: foto grande, frase, telefone e e-mail (o que ela escolheu mostrar). */
export function CartaoContato({ id, nome, cor, onClose, onConversar, onLigar }: { id: string | null; nome: string; cor: string; onClose: () => void;
  onConversar?: () => void; onLigar?: (tipo: 'audio' | 'video') => void }) {
  const c = useContato(id);
  const abrir = (u: string) => semBloqueio(() => Linking.openURL(u)).catch(() => Alert.alert(t('Não foi possível abrir.')));
  const linha = (icone: string, texto: string, acoes: { r: string; u: string }[]) => (
    <View style={{ backgroundColor: C.card, borderRadius: 14, padding: 14, gap: 8 }}>
      <Text style={{ color: C.text, fontSize: 16 }} selectable>{icone} {texto}</Text>
      <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
        {acoes.map(a => <Pressable key={a.r} onPress={() => abrir(a.u)} style={{ paddingHorizontal: 14, paddingVertical: 8, borderRadius: 20, backgroundColor: alpha(cor, 0.18) }}>
          <Text style={{ color: cor, fontWeight: '700' }}>{a.r}</Text></Pressable>)}
      </View>
    </View>
  );
  const tel = c?.telefone ? soDigitos(c.telefone) : '';
  return (
    <Sheet visible={!!id} onClose={onClose} title={t('Contato')}>
      <ScrollView contentContainerStyle={{ padding: 20, gap: 12, alignItems: 'stretch', paddingBottom: 60 }}>
        <View style={{ alignItems: 'center', gap: 8 }}>
          <Avatar id={id} nome={nome} tam={140} cor={cor} ampliar />
          <Text style={{ color: C.text, fontSize: 22, fontWeight: '800', textAlign: 'center' }}>{nome}</Text>
          {c === undefined ? <ActivityIndicator color={cor} />
            : !!c?.frase && <Text style={{ color: C.sub, fontSize: 15, textAlign: 'center', fontStyle: 'italic' }}>“{c.frase}”</Text>}
        </View>
        {(onConversar || onLigar) && (
          <View style={{ flexDirection: 'row', justifyContent: 'center', gap: 12 }}>
            {onConversar && <Pressable onPress={onConversar} style={{ alignItems: 'center', gap: 4, padding: 10 }}><Text style={{ fontSize: 24 }}>💬</Text><Text style={{ color: C.sub, fontSize: 12 }}>{t('Conversar')}</Text></Pressable>}
            {onLigar && <Pressable onPress={() => onLigar('audio')} style={{ alignItems: 'center', gap: 4, padding: 10 }}><Text style={{ fontSize: 24 }}>📞</Text><Text style={{ color: C.sub, fontSize: 12 }}>{t('Áudio')}</Text></Pressable>}
            {onLigar && <Pressable onPress={() => onLigar('video')} style={{ alignItems: 'center', gap: 4, padding: 10 }}><Text style={{ fontSize: 24 }}>🎥</Text><Text style={{ color: C.sub, fontSize: 12 }}>{t('Vídeo')}</Text></Pressable>}
          </View>
        )}
        {!!c?.telefone && linha('📱', c.telefone, [{ r: t('Ligar'), u: `tel:${tel}` }, { r: 'WhatsApp', u: `https://wa.me/${tel.replace('+', '')}` }, { r: 'SMS', u: `sms:${tel}` }])}
        {!!c?.email && linha('✉️', c.email, [{ r: t('Mandar e-mail'), u: `mailto:${c.email}` }])}
        {c === null && <Text style={{ color: C.mute, fontSize: 12, textAlign: 'center' }}>{t('Esta pessoa ainda não preencheu o cartão de contato.')}</Text>}
      </ScrollView>
    </Sheet>
  );
}

/** Meu cartão: foto (galeria ou câmera), telefone, e-mail e frase. */
export function MeuContatoSheet({ visible, nome, cor, onClose }: { visible: boolean; nome: string; cor: string; onClose: () => void }) {
  const [c, setC] = useState<Contato | null>(null);
  const [tel, setTel] = useState(''); const [email, setEmail] = useState(''); const [frase, setFrase] = useState('');
  const [erro, setErro] = useState(''); const [indo, setIndo] = useState(false); const [fotoIndo, setFotoIndo] = useState(false);
  const [menu, setMenu] = useState<Action[] | null>(null);
  // 0.72 (11g): Fotos · Tirar foto · Arquivos, pelo mesmo caminho do menu de anexos (semNuvem: a foto vai para o cartão)
  const anexo = useAnexar({ cor, semNuvem: true });
  const teclado = useTecladoAndroid();
  useEffect(() => {
    if (!visible) return;
    setErro(''); setC(null);
    meuContato().then(x => { setC(x); setTel(x.telefone ?? ''); setEmail(x.email ?? ''); setFrase(x.frase ?? ''); }).catch(e => setErro(String(e?.message ?? e)));
  }, [visible]);
  const foto = async (origem: OrigemAnexo) => {
    if (!c) return;
    try {
      const r = await anexo.anexar({ origem, tipo: 'imagem', frente: true, dica: t('Enquadre o rosto e toque no botão. 🔄 vira a câmera.') });
      const a = r?.anexo;
      if (!a) return;
      setFotoIndo(true);
      setC(await trocarMinhaFoto(a, { ...c, telefone: tel, email, frase }));
    } catch (e: any) { Alert.alert(t('Não foi possível trocar a foto'), String(e?.message ?? e)); }
    finally { setFotoIndo(false); }
  };
  const salvar = async () => {
    setIndo(true); setErro('');
    try { await salvarMeuContato({ telefone: tel, email, frase, foto: c?.foto ?? null }); onClose(); }
    catch (e: any) { setErro(String(e?.message ?? e)); }
    finally { setIndo(false); }
  };
  const opFoto: Action[] = [{ label: '🖼️ ' + t('Fotos'), onPress: () => foto('fotos') },
    ...(Platform.OS !== 'web' ? [{ label: '📷 ' + t('Tirar foto'), onPress: () => foto('camera') }] : []),
    { label: '📎 ' + t('Arquivos'), onPress: () => foto('arquivos') },
    ...(c?.foto ? [{ label: '🗑 ' + t('Tirar a foto'), destructive: true, onPress: () => c && tirarMinhaFoto(c).then(setC).catch(e => Alert.alert(String(e?.message ?? e))) }] : [])];
  return (
    <Sheet visible={visible} onClose={onClose} title={t('Meu cartão de contato')}>
      <ScrollView {...ROLAGEM_TECLADO} keyboardShouldPersistTaps="handled" contentContainerStyle={{ padding: 20, gap: 10, paddingBottom: 60 + teclado }}>
        <Pressable onPress={() => setMenu(opFoto)} disabled={!c || fotoIndo} style={{ alignItems: 'center', gap: 6 }}>
          <View>
            <Avatar nome={nome} tam={130} cor={cor} foto={c?.foto ?? null} />
            <View style={{ position: 'absolute', right: 0, bottom: 0, width: 40, height: 40, borderRadius: 20, backgroundColor: cor, alignItems: 'center', justifyContent: 'center', borderWidth: 3, borderColor: C.bg }}>
              {fotoIndo ? <ActivityIndicator color="#fff" size="small" /> : <Text style={{ fontSize: 18 }}>📷</Text>}
            </View>
          </View>
          <Text style={{ color: cor, fontWeight: '700' }}>{c?.foto ? t('Trocar a foto') : t('Pôr uma foto')}</Text>
        </Pressable>
        <Text style={{ color: C.text, fontSize: 20, fontWeight: '800', textAlign: 'center' }}>{nome}</Text>

        <Text style={{ color: C.sub, fontSize: 12, fontWeight: '700', letterSpacing: 0.8, marginTop: 8 }}>{t('FRASE')}</Text>
        <Field value={frase} onChangeText={x => setFrase(x.slice(0, FRASE_MAX))} placeholder={t('Ex.: Disponível · Em aula até 18h · Bora viajar!')} maxLength={FRASE_MAX} />
        <Text style={{ color: frase.length >= FRASE_MAX ? C.warn : C.mute, fontSize: 11, textAlign: 'right' }}>{frase.length}/{FRASE_MAX}</Text>

        <Text style={{ color: C.sub, fontSize: 12, fontWeight: '700', letterSpacing: 0.8 }}>{t('TELEFONE (OPCIONAL)')}</Text>
        <Field value={tel} onChangeText={setTel} placeholder="+55 11 99999-9999" keyboardType="phone-pad" maxLength={30} />
        <Text style={{ color: C.sub, fontSize: 12, fontWeight: '700', letterSpacing: 0.8, marginTop: 4 }}>{t('E-MAIL (OPCIONAL)')}</Text>
        <Field value={email} onChangeText={setEmail} placeholder={t('seu@email.com')} keyboardType="email-address" autoCapitalize="none" autoCorrect={false} maxLength={120} />

        <Text style={{ color: C.mute, fontSize: 12, marginTop: 4 }}>{t('Quem vê: só quem conversa com você, os seus amigos e quem está num grupo do app com você. Deixe em branco o que não quiser mostrar.')}</Text>
        {!!erro && <Text style={{ color: C.danger }}>{erro}</Text>}
        <Btn cor={cor} label={indo ? t('Aguarde...') : t('Salvar')} disabled={indo || !c} onPress={salvar} />
      </ScrollView>
      {anexo.tela}
      <ActionSheet title={t('Foto do contato')} actions={menu} onClose={() => setMenu(null)} />
    </Sheet>
  );
}
